/**
 * L'ordinamento delle tabelle, tenuto **fuori** dai componenti di proposito.
 *
 * Il frontend non ha un ambiente di prova React: tutto ciò che vive dentro un
 * componente si può solo guardare a schermo, e infatti le trappole
 * dell'interfaccia già pagate sono quasi tutte lì. Qui sta la parte che
 * *decide* — il giro dei tre clic e il confronto fra due celle — e quella si
 * prova davvero, con `test/ordinamento.test.ts`.
 */

/** Il verso dichiarato dal triangolino. */
export type Verso = 'salita' | 'discesa';

export interface Ordinamento {
  /** La colonna toccata per ultima. */
  chiave: string;
  /**
   * ⚠️ `null` NON significa «non ordinata»: significa **«ordinata, ma senza
   * dirlo»**. È il terzo clic — il triangolino si spegne e le righe restano
   * dove sono, invece di saltare di posto sotto gli occhi di chi legge.
   *
   * Chi non ha ancora ordinato niente ha `Ordinamento | null` a `null`, che è
   * un'altra cosa ancora: quello è «mai toccato», e le righe stanno nell'ordine
   * deciso dal server.
   */
  verso: Verso | null;
}

/**
 * Il giro dei clic su una colonna: salita → discesa → spento → salita.
 * Cliccarne un'altra riparte sempre dalla salita.
 */
export function prossimoOrdinamento(attuale: Ordinamento | null, chiave: string): Ordinamento {
  if (attuale === null || attuale.chiave !== chiave) return { chiave, verso: 'salita' };
  if (attuale.verso === 'salita') return { chiave, verso: 'discesa' };
  if (attuale.verso === 'discesa') return { chiave, verso: null };
  return { chiave, verso: 'salita' };
}

/** Quanto vale una cella per l'ordinamento. `null` = vuota. */
export type Valore = string | number | null;

/**
 * ⚠️ **Il verso spento eredita la discesa, e non è un numero a caso.** Il terzo
 * clic arriva sempre dopo il secondo, e il secondo è la discesa: è il giro di
 * `prossimoOrdinamento` a garantirlo, e c'è una prova che lo tiene fermo. Se un
 * domani il giro cambia, quella prova cade prima che cambi il comportamento
 * sotto gli occhi di qualcuno.
 */
const VERSO_QUANDO_SPENTO: Verso = 'discesa';

/**
 * Confronta due celle piene.
 *
 * ⚠️ `localeCompare` in italiano e non `<`: il confronto grezzo guarda i codici
 * dei caratteri, e «Àbaco» finirebbe **dopo** «Zulu». `numeric` fa sì che
 * «Scadenza 2» venga prima di «Scadenza 10» invece che dopo.
 */
function confrontaPieni(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'it', { numeric: true, sensitivity: 'base' });
}

/**
 * Ordina una copia delle righe. L'originale non si tocca: è quello che arriva
 * dal server, ed è anche l'ordine a cui si torna ricaricando.
 *
 * ⚠️ Le celle vuote finiscono **sempre in fondo**, in salita come in discesa.
 * Il vuoto non è «più piccolo di tutto»: è «non pervenuto», e vederselo
 * comparire in cima ordinando per email sarebbe una sorpresa, non un ordine.
 * Per questo il ribaltamento della discesa si applica **dopo** averle messe da
 * parte, e non a tutto il confronto.
 *
 * L'ordinamento di JavaScript è stabile: a parità di cella le righe restano
 * nell'ordine in cui erano arrivate, cioè quello del server.
 */
export function ordinaRighe<T>(
  righe: readonly T[],
  ordinamento: Ordinamento | null,
  valore: (riga: T, chiave: string) => Valore,
): T[] {
  if (ordinamento === null) return [...righe];
  const verso = ordinamento.verso ?? VERSO_QUANDO_SPENTO;

  return [...righe].sort((x, y) => {
    const a = valore(x, ordinamento.chiave);
    const b = valore(y, ordinamento.chiave);

    const aVuota = a === null || a === '';
    const bVuota = b === null || b === '';
    if (aVuota && bVuota) return 0;
    if (aVuota) return 1;
    if (bVuota) return -1;

    const esito = confrontaPieni(a, b);
    return verso === 'salita' ? esito : -esito;
  });
}

/**
 * Il valore di `aria-sort` sull'intestazione, per chi naviga con un lettore di
 * schermo. ⚠️ Col triangolino spento si annuncia `none`: le righe sono ancora
 * ordinate, ma l'interfaccia ha smesso di dichiararlo, e mentire a un lettore
 * di schermo dicendo «discendente» quando a video non c'è nessun segno sarebbe
 * peggio che tacere.
 */
export function ariaSort(ordinamento: Ordinamento | null, chiave: string): 'ascending' | 'descending' | 'none' {
  if (ordinamento === null || ordinamento.chiave !== chiave || ordinamento.verso === null) return 'none';
  return ordinamento.verso === 'salita' ? 'ascending' : 'descending';
}
